import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import {
	CmsField,
	cmsInput,
	CmsSection,
	CmsToolbar,
} from "@/components/admin/cms/cms-kit";
import { PageHead } from "@/components/admin/page-head";
import { useToast } from "@/components/ui/feedback";
import { extractErrorMessage } from "@/hooks/use-auth-api";
import { usePermissions } from "@/hooks/use-permissions";
import { type HomeContent, siteApi } from "@/lib/site/site-api";

/**
 * Longueurs au-delà desquelles les moteurs tronquent l'affichage.
 *
 * Ce ne sont pas des limites strictes : une description coupée reste indexée.
 * Mais la phrase se termine alors par des points de suspension au milieu d'un
 * mot, ce qui donne une impression de négligence au moment précis où le
 * visiteur décide s'il clique.
 */
const MAX_TITRE = 60;
const MAX_DESC = 160;

type PageKey = "about" | "faq" | "made-in-cameroon" | "cgv";

const PAGES: Array<{ key: PageKey; label: string; url: string }> = [
	{ key: "about", label: "À propos", url: "/a-propos" },
	{ key: "faq", label: "Aide & FAQ", url: "/aide" },
	{ key: "made-in-cameroon", label: "Made in Cameroon", url: "/made-in-cameroon" },
	{ key: "cgv", label: "Conditions générales de vente", url: "/cgv" },
];

/** Compteur de caractères, vert tant qu'on tient dans la limite. */
function Compteur({ n, max }: { n: number; max: number }) {
	const trop = n > max;
	return (
		<span
			style={{
				fontSize: 11,
				fontWeight: 600,
				color: trop ? "var(--a-danger)" : "var(--a-ink-2)",
				whiteSpace: "nowrap",
			}}
		>
			{n} / {max}
			{trop && " — sera tronqué"}
		</span>
	);
}

/**
 * Aperçu du résultat tel qu'il apparaîtra dans une page de recherche.
 *
 * Montrer la troncature vaut mieux que l'annoncer : on comprend d'un coup
 * d'œil ce que le visiteur lira réellement avant de cliquer.
 */
function ApercuRecherche({
	titre,
	description,
	url,
}: {
	titre: string;
	description: string;
	url: string;
}) {
	const coupe = (t: string, max: number) =>
		t.length > max ? `${t.slice(0, max - 1).trimEnd()}…` : t;

	return (
		<div
			style={{
				background: "var(--a-bg)",
				border: "1px solid var(--a-border)",
				borderRadius: 8,
				padding: "11px 13px",
				marginTop: 10,
			}}
		>
			<div className="muted" style={{ fontSize: 10.5, marginBottom: 6 }}>
				Aperçu dans les résultats de recherche
			</div>
			<div style={{ fontSize: 11.5, color: "var(--a-ink-2)" }}>
				akwaba.cm{url}
			</div>
			<div
				style={{
					fontSize: 15,
					color: "#1a0dab",
					lineHeight: 1.3,
					margin: "2px 0 3px",
				}}
			>
				{coupe(titre, MAX_TITRE) || "— titre manquant —"}
			</div>
			<div style={{ fontSize: 12, color: "var(--a-ink-2)", lineHeight: 1.45 }}>
				{coupe(description, MAX_DESC) || "— description manquante —"}
			</div>
		</div>
	);
}

/**
 * Référencement de tous les contenus éditoriaux, au même endroit.
 *
 * Ces métadonnées étaient réparties sur cinq écrans : celui de l'accueil et
 * ceux des quatre pages d'information. Rien ne permettait de vérifier d'un
 * coup d'œil qu'aucune page ne partait sans titre — et il en manquait.
 */
export function AdminCmsSeoPage() {
	const qc = useQueryClient();
	const toast = useToast();
	const { hasAny, isSuperAdmin } = usePermissions();
	const peutEcrire = isSuperAdmin || hasAny("MARKETING.CMS.WRITE");

	const requetes = useQueries({
		queries: [
			{ queryKey: ["home-content"], queryFn: siteApi.getHome },
			...PAGES.map((p) => ({
				queryKey: ["site-page", p.key],
				queryFn: () => siteApi.getPage(p.key),
			})),
		],
	});

	const home = requetes[0];
	const chargement = requetes.some((r) => r.isLoading);

	const [homeSeo, setHomeSeo] = useState({ title: "", desc: "", keywords: "" });
	const [pagesSeo, setPagesSeo] = useState<
		Record<string, { title: string; desc: string }>
	>({});
	const initial = useRef(true);
	const [dirty, setDirty] = useState(false);

	useEffect(() => {
		if (chargement) return;
		initial.current = true;
		// `useQueries` renvoie l'union des types de chaque requête : on rétablit
		// celui de l'accueil, connu par construction (première entrée).
		const h = home.data as HomeContent | undefined;
		if (h) {
			setHomeSeo({
				title: h.seoTitle ?? "",
				desc: h.seoDescription ?? "",
				keywords: (h.seoKeywords ?? []).join(", "),
			});
		}
		const next: Record<string, { title: string; desc: string }> = {};
		PAGES.forEach((p, i) => {
			// Le référencement suit la langue française : c'est elle qui est
			// indexée pour le Cameroun, et la version anglaise reprend le même
			// contenu tant qu'elle n'est pas publiée séparément.
			const fr = (requetes[i + 1].data as Record<string, Record<string, unknown>>)
				?.fr;
			next[p.key] = {
				title: (fr?.seoTitle as string) ?? "",
				desc: (fr?.seoDescription as string) ?? "",
			};
		});
		setPagesSeo(next);
		setDirty(false);
		const t = window.setTimeout(() => {
			initial.current = false;
		}, 0);
		return () => window.clearTimeout(t);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [chargement, home.data]);

	const marquer = () => {
		if (!initial.current) setDirty(true);
	};

	const save = useMutation({
		mutationFn: async () => {
			await siteApi.updateHome({
				seoTitle: homeSeo.title || null,
				seoDescription: homeSeo.desc || null,
				seoKeywords: homeSeo.keywords
					.split(",")
					.map((k) => k.trim())
					.filter(Boolean),
			});
			// Les pages sont enregistrées une par une : l'API n'expose pas
			// d'écriture groupée, et un échec partiel doit rester identifiable.
			for (let i = 0; i < PAGES.length; i += 1) {
				const p = PAGES[i];
				const courant = requetes[i + 1].data as Record<
					string,
					Record<string, unknown>
				>;
				const seo = pagesSeo[p.key];
				if (!courant || !seo) continue;
				await siteApi.updatePage(p.key, {
					...courant,
					fr: { ...(courant.fr ?? {}), seoTitle: seo.title, seoDescription: seo.desc },
				});
			}
		},
		onSuccess: () => {
			qc.invalidateQueries({ queryKey: ["home-content"] });
			PAGES.forEach((p) =>
				qc.invalidateQueries({ queryKey: ["site-page", p.key] }),
			);
			setDirty(false);
			toast.success("Référencement mis à jour.");
		},
		onError: (e) =>
			toast.error(extractErrorMessage(e, "Enregistrement impossible")),
	});

	const manquants = [
		...(homeSeo.title && homeSeo.desc ? [] : ["Page d’accueil"]),
		...PAGES.filter(
			(p) => !pagesSeo[p.key]?.title || !pagesSeo[p.key]?.desc,
		).map((p) => p.label),
	];

	return (
		<>
			<PageHead
				title="Référencement"
				sub="Titres et descriptions affichés dans les résultats de recherche"
			/>

			<CmsToolbar
				title="Métadonnées des pages"
				subtitle={
					peutEcrire
						? "Un titre court et une description qui donne envie de cliquer valent mieux qu’une liste de mots-clés."
						: "Lecture seule : la permission « CMS » est requise pour modifier."
				}
				dirty={dirty}
				saving={save.isPending}
				onSave={peutEcrire ? () => save.mutate() : undefined}
				saveLabel="Enregistrer le référencement"
			/>

			{manquants.length > 0 && !chargement && (
				<div
					style={{
						background: "var(--a-bg-2)",
						border: "1px solid var(--a-border)",
						borderLeft: "3px solid var(--a-danger)",
						borderRadius: 8,
						padding: "10px 12px",
						marginBottom: 12,
						fontSize: 12,
						lineHeight: 1.5,
					}}
					data-testid="seo-manquants"
				>
					<strong>Référencement incomplet</strong> — sans titre ni description,
					le moteur en invente à partir du contenu de la page, et le résultat
					est rarement flatteur. À compléter : {manquants.join(", ")}.
				</div>
			)}

			{chargement ? (
				<div className="card">
					<div className="card-b">
						<span className="muted">Chargement…</span>
					</div>
				</div>
			) : (
				<>
					<CmsSection
						title="Page d’accueil"
						where="La page la plus visitée : c’est elle qui porte le nom de la marque"
						icon="home"
						defaultOpen
					>
						<CmsField
							label="Titre"
							hint="Ce que l’on lit en bleu dans les résultats, et dans l’onglet du navigateur"
						>
							<div
								style={{
									display: "flex",
									flexWrap: "wrap",
									alignItems: "center",
									gap: 8,
								}}
							>
								<input
									style={{ ...cmsInput, flex: "1 1 240px" }}
									disabled={!peutEcrire}
									value={homeSeo.title}
									onChange={(e) => {
										marquer();
										setHomeSeo((v) => ({ ...v, title: e.target.value }));
									}}
								/>
								<Compteur n={homeSeo.title.length} max={MAX_TITRE} />
							</div>
						</CmsField>
						<div style={{ height: 12 }} />
						<CmsField
							label="Description"
							hint="Le texte gris sous le titre. Deux phrases qui disent ce qu’on trouve ici."
						>
							<textarea
								style={{ ...cmsInput, minHeight: 64, resize: "vertical" }}
								disabled={!peutEcrire}
								value={homeSeo.desc}
								onChange={(e) => {
									marquer();
									setHomeSeo((v) => ({ ...v, desc: e.target.value }));
								}}
							/>
							<div style={{ marginTop: 4, textAlign: "right" }}>
								<Compteur n={homeSeo.desc.length} max={MAX_DESC} />
							</div>
						</CmsField>
						<div style={{ height: 12 }} />
						<CmsField
							label="Mots-clés"
							hint="Séparés par des virgules. Les moteurs n’en tiennent plus compte pour le classement ; ils servent surtout de repère interne."
						>
							<input
								style={cmsInput}
								disabled={!peutEcrire}
								value={homeSeo.keywords}
								onChange={(e) => {
									marquer();
									setHomeSeo((v) => ({ ...v, keywords: e.target.value }));
								}}
							/>
						</CmsField>
						<ApercuRecherche
							titre={homeSeo.title}
							description={homeSeo.desc}
							url="/"
						/>
					</CmsSection>

					{PAGES.map((p) => {
						const seo = pagesSeo[p.key] ?? { title: "", desc: "" };
						const incomplet = !seo.title || !seo.desc;
						return (
							<CmsSection
								key={p.key}
								title={p.label}
								where={`Page ${p.url}${incomplet ? " — référencement incomplet" : ""}`}
								icon="layout"
							>
								<CmsField label="Titre">
									<div
										style={{
											display: "flex",
											flexWrap: "wrap",
											alignItems: "center",
											gap: 8,
										}}
									>
										<input
											style={{ ...cmsInput, flex: "1 1 240px" }}
											disabled={!peutEcrire}
											value={seo.title}
											onChange={(e) => {
												marquer();
												setPagesSeo((v) => ({
													...v,
													[p.key]: { ...v[p.key], title: e.target.value },
												}));
											}}
										/>
										<Compteur n={seo.title.length} max={MAX_TITRE} />
									</div>
								</CmsField>
								<div style={{ height: 12 }} />
								<CmsField label="Description">
									<textarea
										style={{ ...cmsInput, minHeight: 60, resize: "vertical" }}
										disabled={!peutEcrire}
										value={seo.desc}
										onChange={(e) => {
											marquer();
											setPagesSeo((v) => ({
												...v,
												[p.key]: { ...v[p.key], desc: e.target.value },
											}));
										}}
									/>
									<div style={{ marginTop: 4, textAlign: "right" }}>
										<Compteur n={seo.desc.length} max={MAX_DESC} />
									</div>
								</CmsField>
								<ApercuRecherche
									titre={seo.title}
									description={seo.desc}
									url={p.url}
								/>
							</CmsSection>
						);
					})}
				</>
			)}
		</>
	);
}
